import { emit } from './emit'
import { getPageViewId } from './pageViewId'
import { newUuid } from './uuid'
import type { Sku, SkuPriceOption, SkuPriceOptionsResponse, SkuPricesResponse } from '@/types/interfaces/sku'

/** Pricing + availability instrumentation for <VisitOptionsPreview>.
 *  Fires once per (page_view × sku × page_location) after availability resolves.
 *  Prices in integer cents; no PII added beyond Segment's auto-attached identifiers. */

export type VisitOptionsPageLocation = 'sku_detail' | 'sku_refresh_header' | 'ssr_urgent_care' | (string & {})

/** Dedup keyed by `${page_view_id}:${sku_id}:${page_location}` — survives strict-mode remounts. */
const emittedKeys = new Set<string>()

export function _resetVisitOptionsDedupForTests(): void {
    emittedKeys.clear()
}

export type VisitOptionType = 'video' | 'chat' | 'econsult' | 'in_person'

interface VisitTypePayload {
    shown: boolean
    cash_cents: number | null
    insurance_cents: number | null
    preferred_price_type: 'insurance' | 'cash' | null
    availability_promise: string | null
}

function flattenPrices(response: SkuPriceOptionsResponse | null, skuId: string | number): SkuPriceOption[] {
    if (!response?.sku_price_mapping) return []
    const mapping = response.sku_price_mapping.find((m) => String(m.id) === String(skuId))
    if (!mapping) return []
    const raw = mapping.prices
    const arr: SkuPricesResponse[] = Array.isArray(raw) ? raw : raw ? [raw as SkuPricesResponse] : []
    return arr.flatMap((p) => p.options || [])
}

function buildVisitTypePayload(
    options: SkuPriceOption[],
    type: VisitOptionType,
    availabilityPromise: string | null | undefined,
): VisitTypePayload {
    const option = options.find((o) => o.type === type)
    const cashCents = option?.cash ?? null
    const insuranceCents = option?.insurance ?? null
    const hasPrice = cashCents != null || insuranceCents != null

    let preferred: 'insurance' | 'cash' | null = null
    if (insuranceCents != null && cashCents != null) {
        preferred = insuranceCents < cashCents ? 'insurance' : 'cash'
    } else if (insuranceCents != null) {
        preferred = 'insurance'
    } else if (cashCents != null) {
        preferred = 'cash'
    }

    return {
        shown: hasPrice,
        cash_cents: cashCents,
        insurance_cents: insuranceCents,
        preferred_price_type: preferred,
        availability_promise: availabilityPromise ?? null,
    }
}

interface TrackArgs {
    sku: Sku
    response: SkuPriceOptionsResponse | null
    pageLocation: VisitOptionsPageLocation
    availableVisitTypes: VisitOptionType[]
    videoAvailability: string | null | undefined
    chatAvailability: string | null | undefined
    isAuthed: boolean
    isMedicationSku: boolean
}

export function trackVisitOptionsPreviewShown(args: TrackArgs): void {
    const pageViewId = getPageViewId() ?? ''
    const dedupKey = `${pageViewId}:${String(args.sku.id)}:${args.pageLocation}`
    if (emittedKeys.has(dedupKey)) return
    emittedKeys.add(dedupKey)

    const options = flattenPrices(args.response, args.sku.id)
    const video = buildVisitTypePayload(options, 'video', args.videoAvailability)
    const chat = buildVisitTypePayload(options, 'chat', args.chatAvailability)

    // Drop modality payload when the modality isn't on offer (avoids null-bloat rows).
    const videoEligible = args.availableVisitTypes.includes('video')
    const chatEligible = args.availableVisitTypes.includes('chat')

    // No `path` / `sku_hyphenated_name`: warehouse joins sku_id to the
    // SKUs table to recover slug. Keeps the medical-service slug out of
    // the payload Segment auto-decorates with userId post-identify.
    emit('visit_options_preview_shown', {
        preview_view_id: newUuid(),
        page_view_id: pageViewId || null,
        page_location: args.pageLocation,
        sku_id: String(args.sku.id),
        available_visit_types: args.availableVisitTypes,
        video: videoEligible ? video : null,
        chat: chatEligible ? chat : null,
        is_authed: args.isAuthed,
        is_medication_sku: args.isMedicationSku,
    })
}
